
import React, { useState, useEffect } from 'react';
import { updateSEO, addJsonLd } from '../../utils/seo';

interface Product {
  id: string;
  name: string;
  article: string;
  format: string;
  surface: string;
  pricePerM2: number;
  inStock: number;
  brand: string;
  image: string;
  description: string;
}

const CatalogPage: React.FC = () => {
  const [selectedBrand, setSelectedBrand] = useState('all');
  const [selectedFormat, setSelectedFormat] = useState('all');
  const [selectedSurface, setSelectedSurface] = useState('all');
  const [showCalculator, setShowCalculator] = useState<string | null>(null);
  const [calculatorData, setCalculatorData] = useState({
    length: '',
    width: '',
    area: ''
  });

  const products: Product[] = [
    {
      id: '1',
      name: 'Керамогранит Carrara White',
      article: 'D6605',
      format: '60×60',
      surface: 'Матовая',
      pricePerM2: 2850,
      inStock: 1250,
      brand: 'Terramic',
      image: 'https://public.readdy.ai/ai/img_res/880f47ed81239421c66f74cdeae681f8.jpg',
      description: 'Элегантный керамогранит под мрамор Каррара с натуральными прожилками'
    },
    {
      id: '2',
      name: 'Керамогранит Emperador Dark',
      article: 'D12503',
      format: '120×60',
      surface: 'Матовая',
      pricePerM2: 3200,
      inStock: 890,
      brand: 'Terramic',
      image: 'https://readdy.ai/api/search-image?query=Dark%20brown%20marble-look%20ceramic%20tile%20with%20golden%20veining%2C%20emperador%20marble%20pattern%2C%20matte%20finish%2C%20large%20format%20120x60%2C%20luxury%20floor%20tile%2C%20rich%20brown%20background%20with%20gold%20accents&width=400&height=400&seq=tile-2&orientation=squarish',
      description: 'Роскошный темный керамогранит под мрамор Emperador с золотистыми прожилками'
    },
    {
      id: '3',
      name: 'Керамогранит Travertine Beige',
      article: 'F69103',
      format: '60×60',
      surface: 'Глянцевая',
      pricePerM2: 2650,
      inStock: 2100,
      brand: 'Wan SHENG',
      image: 'https://readdy.ai/api/search-image?query=Beige%20travertine-look%20ceramic%20tile%20with%20natural%20stone%20texture%2C%20glossy%20finish%2C%2060x60%20format%2C%20warm%20beige%20color%2C%20natural%20stone%20pattern%2C%20elegant%20floor%20tile&width=400&height=400&seq=tile-3&orientation=squarish',
      description: 'Теплый бежевый керамогранит под травертин с натуральной текстурой'
    },
    {
      id: '4',
      name: 'Керамогранит Concrete Grey',
      article: 'D12557',
      format: '120×60',
      surface: 'Матовая',
      pricePerM2: 2950,
      inStock: 1560,
      brand: 'MinTao',
      image: 'https://readdy.ai/api/search-image?query=Modern%20grey%20concrete-look%20ceramic%20tile%2C%20industrial%20style%2C%20matte%20finish%2C%20large%20format%20120x60%2C%20contemporary%20floor%20tile%2C%20smooth%20grey%20surface%20with%20subtle%20texture&width=400&height=400&seq=tile-4&orientation=squarish',
      description: 'Современный серый керамогранит под бетон в индустриальном стиле'
    },
    {
      id: '5',
      name: 'Керамогранит Calacatta Gold',
      article: 'D6601',
      format: '60×60',
      surface: 'Глянцевая',
      pricePerM2: 3450,
      inStock: 750,
      brand: 'Terramic',
      image: 'https://readdy.ai/api/search-image?query=Luxury%20white%20marble-look%20ceramic%20tile%20with%20bold%20gold%20veining%2C%20Calacatta%20marble%20pattern%2C%20glossy%20finish%2C%2060x60%20format%2C%20premium%20floor%20tile%2C%20white%20background%20with%20dramatic%20gold%20veins&width=400&height=400&seq=tile-5&orientation=squarish',
      description: 'Премиальный белый керамогранит под мрамор Калакатта с золотыми прожилками'
    },
    {
      id: '6',
      name: 'Керамогранит Wood Oak',
      article: 'D12521',
      format: '120×60',
      surface: 'Матовая',
      pricePerM2: 2750,
      inStock: 1890,
      brand: 'MinTao',
      image: 'https://readdy.ai/api/search-image?query=Natural%20oak%20wood-look%20ceramic%20tile%2C%20realistic%20wood%20grain%20texture%2C%20matte%20finish%2C%20large%20format%20120x60%2C%20warm%20brown%20wood%20pattern%2C%20modern%20wood-effect%20floor%20tile&width=400&height=400&seq=tile-6&orientation=squarish',
      description: 'Керамогранит под натуральный дуб с реалистичной текстурой дерева'
    }
  ];

  useEffect(() => {
    // Update SEO
    updateSEO({
      title: 'Каталог керамогранита в наличии - MUSLIM CERAMICA Костанай',
      description: 'Полный каталог керамогранита в наличии в Костанае. Более 25 000 м² плитки Terramic, Wan SHENG, MinTao. Цены за м², калькулятор количества, быстрая доставка.',
      keywords: 'каталог керамогранита, плитка в наличии Костанай, цены керамогранит, Terramic каталог, Wan SHENG, MinTao',
      canonical: '/catalog'
    });

    // Add Product Catalog Schema
    addJsonLd({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Каталог керамогранита MUSLIM CERAMICA",
      "description": "Полный каталог керамогранита в наличии в Костанае",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/catalog`,
      "numberOfItems": products.length,
      "itemListElement": products.map((product, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": product.name,
          "description": product.description,
          "brand": {
            "@type": "Brand",
            "name": product.brand
          },
          "sku": product.article,
          "image": product.image,
          "offers": {
            "@type": "Offer",
            "price": product.pricePerM2,
            "priceCurrency": "KZT",
            "availability": product.inStock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            "seller": {
              "@type": "Organization",
              "name": "MUSLIM CERAMICA"
            }
          },
          "additionalProperty": [
            {
              "@type": "PropertyValue",
              "name": "Формат",
              "value": product.format
            },
            {
              "@type": "PropertyValue", 
              "name": "Поверхность",
              "value": product.surface
            },
            {
              "@type": "PropertyValue",
              "name": "Наличие",
              "value": `${product.inStock} м²`
            }
          ]
        }
      }))
    });
  }, []);

  const filteredProducts = products.filter(product => {
    return (selectedBrand === 'all' || product.brand === selectedBrand) &&
           (selectedFormat === 'all' || product.format === selectedFormat) &&
           (selectedSurface === 'all' || product.surface === selectedSurface);
  });

  const calculateArea = (length: string, width: string) => {
    const l = parseFloat(length);
    const w = parseFloat(width);
    if (l && w) {
      return (l * w).toFixed(2);
    }
    return '';
  };

  const calculateQuantity = (area: string, product: Product) => {
    const areaNum = parseFloat(area);
    if (areaNum) {
      const tileArea = product.format === '60×60' ? 0.36 : 0.72; // м² на плитку
      const quantity = Math.ceil(areaNum / tileArea);
      const totalPrice = areaNum * product.pricePerM2;
      return { quantity, totalPrice, area: areaNum };
    }
    return null;
  };

  const openQuiz = (productName?: string) => {
    const message = productName 
      ? `Здравствуйте! Интересует ${productName}. Хочу получить консультацию и узнать цену.`
      : 'Здравствуйте! Хочу получить консультацию по керамограниту.';
    
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/77085773668?text=${encodedMessage}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-green-700 py-12">
        <div className="container mx-auto px-6">
          <div className="text-center text-white">
            <h1 className="text-4xl font-bold mb-4">Каталог керамогранита в наличии</h1>
            <p className="text-xl text-green-100">Более 25 000 м² плитки на складе в Костанае</p>
            <div className="flex justify-center gap-6 mt-6 text-sm">
              <div className="flex items-center gap-2">
                <i className="ri-check-line text-green-300"></i>
                <span>Цены за м²</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-check-line text-green-300"></i>
                <span>Калькулятор количества</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-check-line text-green-300"></i>
                <span>Наличие на складе</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap gap-4 justify-center">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Бренд</label>
              <select 
                value={selectedBrand}
                onChange={(e) => setSelectedBrand(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 pr-8"
              >
                <option value="all">Все бренды</option>
                <option value="Terramic">Terramic</option>
                <option value="Wan SHENG">Wan SHENG</option>
                <option value="MinTao">MinTao</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Формат</label>
              <select 
                value={selectedFormat}
                onChange={(e) => setSelectedFormat(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 pr-8"
              >
                <option value="all">Все форматы</option>
                <option value="60×60">60×60</option>
                <option value="120×60">120×60</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Поверхность</label>
              <select 
                value={selectedSurface}
                onChange={(e) => setSelectedSurface(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-green-500 pr-8"
              >
                <option value="all">Все поверхности</option>
                <option value="Матовая">Матовая</option>
                <option value="Глянцевая">Глянцевая</option>
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12" data-product-shop="true">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow" data-product-shop="true">
              <div className="relative h-48 overflow-hidden">
                <img 
                  alt="Керамическая плитка под дерево" 
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" 
                  src="https://readdy.ai/api/search-image?query=Realistic%20wood-look%20ceramic%20tile%20flooring%20displayed%20in%20Kostanay%20showroom%2C%20natural%20wood%20texture%20porcelain%20tiles%2C%20warm%20brown%20wooden%20floor%20tiles%20for%20Kostanay%20homes&width=400&height=300&seq=kostanay-wood-tiles&orientation=landscape"
                />
                <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">✅ В наличии</div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Под дерево</h3>
                <p className="text-gray-600 mb-3">15 текстур в наличии</p>
                <div className="text-sm text-blue-600 mb-3">Завод-производитель</div>
                <button 
                  onClick={() => openQuiz('керамическая плитка под дерево от ведущего завода. Хочу посмотреть варианты.')}
                  className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Посмотреть варианты
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow" data-product-shop="true">
              <div className="relative h-48 overflow-hidden">
                <img 
                  alt="Керамическая плитка под мрамор" 
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" 
                  src="https://readdy.ai/api/search-image?query=Realistic%20white%20marble%20texture%20ceramic%20tile%20in%20Kostanay%20showroom%2C%20elegant%20marble%20veining%20pattern%2C%20luxury%20white%20marble%20surface%20for%20Kostanay%20interiors&width=400&height=300&seq=kostanay-marble-tiles&orientation=landscape"
                />
                <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">✅ В наличии</div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Под мрамор</h3>
                <p className="text-gray-600 mb-3">20 дизайнов в наличии</p>
                <div className="text-sm text-blue-600 mb-3">Завод-производитель</div>
                <button 
                  onClick={() => openQuiz('керамическая плитка под мрамор от ведущего завода. Хочу посмотреть варианты.')}
                  className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Посмотреть варианты
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow" data-product-shop="true">
              <div className="relative h-48 overflow-hidden">
                <img 
                  alt="Керамическая плитка под бетон" 
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" 
                  src="https://readdy.ai/api/search-image?query=Realistic%20concrete%20texture%20ceramic%20tile%20in%20Kostanay%20warehouse%2C%20industrial%20grey%20concrete%20surface%2C%20modern%20loft%20style%20wall%20covering%20for%20Kostanay%20buildings&width=400&height=300&seq=kostanay-concrete-tiles&orientation=landscape"
                />
                <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">✅ В наличии</div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Под бетон</h3>
                <p className="text-gray-600 mb-3">12 вариантов в наличии</p>
                <div className="text-sm text-blue-600 mb-3">Завод-производитель</div>
                <button 
                  onClick={() => openQuiz('керамическая плитка под бетон от ведущего завода. Хочу посмотреть варианты.')}
                  className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Посмотреть варианты
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow" data-product-shop="true">
              <div className="relative h-48 overflow-hidden">
                <img 
                  alt="Керамическая плитка под камень" 
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-300" 
                  src="https://readdy.ai/api/search-image?query=Realistic%20natural%20stone%20texture%20ceramic%20tile%20in%20Kostanay%20showroom%2C%20granite%20surface%20pattern%2C%20luxury%20stone%20effect%20flooring%20for%20Kostanay%20construction%20projects&width=400&height=300&seq=kostanay-stone-tiles&orientation=landscape"
                />
                <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">✅ В наличии</div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-2">Под камень</h3>
                <p className="text-gray-600 mb-3">18 текстур в наличии</p>
                <div className="text-sm text-blue-600 mb-3">Завод-производитель</div>
                <button 
                  onClick={() => openQuiz('керамическая плитка под камень от ведущего завода. Хочу посмотреть варианты.')}
                  className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Посмотреть варианты
                </button>
              </div>
            </div>
          </div>

          {/* Original product cards remain unchanged */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product) => (
              <div key={product.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                <div className="relative">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-full h-64 object-cover object-top"
                  />
                  <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {product.brand}
                  </div>
                  <div className="absolute top-4 right-4 bg-white px-3 py-1 rounded-full text-sm font-semibold text-gray-800">
                    {product.format}
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-semibold mb-2 text-gray-800">{product.name}</h3>
                  <p className="text-gray-600 mb-3">{product.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Артикул:</span>
                      <span className="font-semibold">{product.article}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Поверхность:</span>
                      <span className="font-semibold">{product.surface}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Наличие:</span>
                      <span className={`font-semibold ${product.inStock > 500 ? 'text-green-600' : product.inStock > 100 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {product.inStock > 500 ? '✅ Много' : product.inStock > 100 ? '⚠️ Ограничено' : '🔴 Мало'} ({product.inStock} м²)
                      </span>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <span className="text-lg font-semibold text-green-700">Узнать цену</span>
                      </div>
                      <button 
                        onClick={() => setShowCalculator(showCalculator === product.id ? null : product.id)}
                        className="text-green-600 hover:text-green-700 font-semibold text-sm cursor-pointer"
                      >
                        📐 Калькулятор
                      </button>
                    </div>

                    {showCalculator === product.id && (
                      <div className="bg-gray-50 p-4 rounded-lg mb-4">
                        <h4 className="font-semibold mb-3 text-gray-800">Расчет количества</h4>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <input 
                            type="number"
                            placeholder="Длина (м)"
                            value={calculatorData.length}
                            onChange={(e) => {
                              const newData = { ...calculatorData, length: e.target.value };
                              const area = calculateArea(newData.length, newData.width);
                              setCalculatorData({ ...newData, area });
                            }}
                            className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-green-500"
                          />
                          <input 
                            type="number"
                            placeholder="Ширина (м)"
                            value={calculatorData.width}
                            onChange={(e) => {
                              const newData = { ...calculatorData, width: e.target.value };
                              const area = calculateArea(newData.length, newData.width);
                              setCalculatorData({ ...newData, area });
                            }}
                            className="px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-green-500"
                          />
                        </div>
                        <input 
                          type="number"
                          placeholder="Или введите площадь (м²)"
                          value={calculatorData.area}
                          onChange={(e) => setCalculatorData({ ...calculatorData, area: e.target.value, length: '', width: '' })}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:border-green-500 mb-3"
                        />
                        
                        {calculatorData.area && (
                          <div className="bg-white p-3 rounded border">
                            {(() => {
                              const calc = calculateQuantity(calculatorData.area, product);
                              return calc ? (
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between">
                                    <span>Площадь:</span>
                                    <span className="font-semibold">{calc.area} м²</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Количество плиток:</span>
                                    <span className="font-semibold">{calc.quantity} шт</span>
                                  </div>
                                  <div className="flex justify-between border-t pt-1">
                                    <span>Получить расчет стоимости:</span>
                                    <span className="font-bold text-green-700">WhatsApp</span>
                                  </div>
                                </div>
                              ) : null;
                            })()}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <button 
                        onClick={() => openQuiz(product.name)}
                        className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap text-sm"
                      >
                        🛒 Заказать в 1 клик
                      </button>
                      <button 
                        onClick={() => openQuiz(product.name)}
                        className="px-4 py-3 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-whatsapp-line text-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-green-700 mb-12">Официальный представитель ведущих брендов</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg text-center shadow-md hover:shadow-lg transition-shadow">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-green-600">T</span>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Terramic</h3>
              <p className="text-gray-600 mb-4">Лидер производства керамогранита с мраморными текстурами</p>
              <div className="text-sm text-gray-500">
                <p>• Более 1 500 000 м² в год</p>
                <p>• Международные сертификаты</p>
                <p>• Инновационные технологии</p>
              </div>
            </div>
            <div className="bg-white p-8 rounded-lg text-center shadow-md hover:shadow-lg transition-shadow">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-green-600">W</span>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Wan SHENG</h3>
              <p className="text-gray-600 mb-4">Премиальная керамика с натуральными текстурами</p>
              <div className="text-sm text-gray-500">
                <p>• Экологически чистое производство</p>
                <p>• Уникальные дизайны</p>
                <p>• Высокая прочность</p>
              </div>
            </div>
            <div className="bg-white p-8 rounded-lg text-center shadow-md hover:shadow-lg transition-shadow">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-2xl font-bold text-green-600">M</span>
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">MinTao</h3>
              <p className="text-gray-600 mb-4">Современные решения для коммерческих объектов</p>
              <div className="text-sm text-gray-500">
                <p>• Повышенная износостойкость</p>
                <p>• Большие форматы</p>
                <p>• Антискользящие покрытия</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-3xl font-bold text-center text-green-700 mb-12">Часто задаваемые вопросы</h2>
          <div className="max-w-4xl mx-auto space-y-4">
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-2">Какие форматы керамической плитки у вас есть?</h3>
                <p className="text-gray-700">У нас в наличии керамическая плитка форматов 60×60 см и 120×60 см. Матовые и глянцевые поверхности, более 60 различных дизайнов.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-2">Сколько стоит керамическая плитка?</h3>
                <p className="text-gray-700">Стоимость зависит от формата и дизайна. Для получения актуальных цен свяжитесь с нами через WhatsApp. Для крупных заказов действуют специальные условия.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-2">Есть ли доставка по области?</h3>
                <p className="text-gray-700">Да, доставляем по всей Костанайской области: Костанай, Рудный, Лисаковск, Аркалык и все районы. Стоимость доставки рассчитывается индивидуально.</p>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6">
                <h3 className="text-lg font-semibold mb-2">Можно ли посмотреть образцы?</h3>
                <p className="text-gray-700">Конечно! Приезжайте в наш шоурум по адресу ул. Карбышева 8/1 в Костанае. Работаем ежедневно с 9:00 до 19:00.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-green-700">
        <div className="container mx-auto px-6 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Нужна консультация по выбору?</h2>
          <p className="text-xl mb-6 text-green-100">Наши специалисты помогут подобрать идеальную плитку для вашего проекта</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => openQuiz()}
              className="bg-white text-green-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              💬 Получить консультацию
            </button>
            <a 
              href="https://wa.me/77085773668"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-green-700 transition-colors whitespace-nowrap"
            >
              📱 WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CatalogPage;
